import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTenants, useTenant, useDeleteDashboard, useRemoveMember, useDocumentDownload } from '@/api/hooks';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Tooltip } from '@/components/Tooltip';
import { useSelection, SelectionBar } from '@/components/selection';
import { Skeleton, EmptyState } from '@/components/primitives';
import {
  EditDashboardDialog,
  AddMemberDialog,
  EditRoleDialog,
  CopyDashboardDialog,
  type EditableDashboard,
  type RoleEditableMember,
} from '@/features/tenants/tenant-actions';
import { toast } from 'react-hot-toast';
import { Search, Users, LayoutDashboard, AlertCircle, ExternalLink, ArrowLeft, Pencil, Trash2, UserCog, UserPlus, FileText, Link2, Download, Lock, Activity, Package, Loader2, CopyPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TenantDocument } from '@/api/client';

const MC_BASE = 'https://missioncontrol.knocking.com';

function fmtBytes(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Tenant documents: secure downloads for files (signed URL via BFF), direct links for LINK kind. */
function DocumentsCard({ tenantId, documents }: { tenantId: string; documents: TenantDocument[] }) {
  const download = useDocumentDownload(tenantId);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const sorted = [...documents].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.title ?? '').localeCompare(b.title ?? '')
  );

  const handleDownload = async (d: TenantDocument) => {
    setPendingId(d.id);
    try {
      const url = await download.mutateAsync({
        documentID: d.id,
        privacyLevel: d.privacyLevel,
        fileName: d.file?.fileName,
      });
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Documents</h3>
        <Badge variant="secondary">{documents.length}</Badge>
      </div>
      <div className="divide-y divide-border/60">
        {sorted.map((d) => {
          const name = d.title || d.file?.fileName || 'Untitled';
          const isLink = d.documentKind === 'LINK';
          return (
            <div key={d.id} className="group flex items-center gap-2.5 py-2.5 px-1 transition-colors hover:bg-muted/40">
              {isLink ? (
                <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium truncate" title={name}>{name}</span>
                <span className="block text-[10px] text-muted-foreground truncate">
                  {[fmtBytes(d.file?.fileSizeInBytes), d.file?.contentType].filter(Boolean).join(' · ')}
                </span>
              </span>
              {d.privacyLevel === 'PROTECTED' && (
                <Tooltip label="Restricted — limited to certain users">
                  <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-medium text-amber-600 dark:text-amber-400">
                    <Lock className="h-2.5 w-2.5" />
                    Restricted
                  </span>
                </Tooltip>
              )}
              {isLink && d.link?.url ? (
                <a
                  href={d.link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-background transition-colors shrink-0"
                  aria-label={`Open ${name}`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : (
                <Tooltip label="Download file">
                  <button
                    type="button"
                    onClick={() => handleDownload(d)}
                    disabled={pendingId === d.id}
                    className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-background transition-colors shrink-0 disabled:opacity-50"
                    aria-label={`Download ${name}`}
                  >
                    {pendingId === d.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                  </button>
                </Tooltip>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function LiveViewsCard({ tenantId, items }: { tenantId: string; items: Array<{ id: string; shopAnalytics: { id: string; title: string | null } | null }> }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Live Views</h3>
        <Badge variant="secondary">{items.length}</Badge>
      </div>
      <div className="divide-y divide-border/60">
        {items.map((lv) => (
          <div key={lv.id} className="flex items-center gap-2.5 py-2.5 px-1 transition-colors hover:bg-muted/40">
            <span className="min-w-0 flex-1 text-xs font-medium truncate" title={lv.shopAnalytics?.title ?? undefined}>
              {lv.shopAnalytics?.title ?? 'Live View'}
            </span>
            <a
              href={`${MC_BASE}/${tenantId}/analytics${lv.shopAnalytics?.id ? `/${lv.shopAnalytics.id}` : ''}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline shrink-0"
            >
              <ExternalLink className="h-3 w-3" />
              Open
            </a>
          </div>
        ))}
      </div>
    </Card>
  );
}

function InventoryCard({ tenantId, items }: { tenantId: string; items: Array<{ id: string; inventoryGroup: { id: string; title: string | null } | null }> }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Package className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Inventory</h3>
        <Badge variant="secondary">{items.length}</Badge>
      </div>
      <div className="divide-y divide-border/60">
        {items.map((inv) => (
          <div key={inv.id} className="flex items-center gap-2.5 py-2.5 px-1 transition-colors hover:bg-muted/40">
            <span className="min-w-0 flex-1 text-xs font-medium truncate" title={inv.inventoryGroup?.title ?? undefined}>
              {inv.inventoryGroup?.title ?? 'Inventory group'}
            </span>
            <a
              href={`${MC_BASE}/${tenantId}/inventory`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline shrink-0"
            >
              <ExternalLink className="h-3 w-3" />
              Open
            </a>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground text-[11px] font-semibold">
      {initials || '?'}
    </span>
  );
}

const memberName = (m: { profile: { firstname: string | null; lastname: string | null } }) =>
  [m.profile.firstname, m.profile.lastname].filter(Boolean).join(' ') || 'Unknown';

export function TenantInspectorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: tenants, isLoading: tenantsLoading, error: tenantsError } = useTenants();
  const { data: tenant, isLoading: tenantLoading } = useTenant(id || '');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dashboardSearch, setDashboardSearch] = useState('');
  const [memberSearch, setMemberSearch] = useState('');

  const [editDash, setEditDash] = useState<EditableDashboard | null>(null);
  const [deleteDash, setDeleteDash] = useState<{ id: string; name: string } | null>(null);
  const [copyDash, setCopyDash] = useState<Record<string, any> | null>(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [roleMember, setRoleMember] = useState<RoleEditableMember | null>(null);
  const [removeMember, setRemoveMember] = useState<{ id: string; name: string } | null>(null);
  const [batchDashes, setBatchDashes] = useState<string[] | null>(null);
  const [batchMembers, setBatchMembers] = useState<{ id: string; name: string }[] | null>(null);

  const dashSel = useSelection();
  const memberSel = useSelection();

  const deleteDashboard = useDeleteDashboard();
  const removeMemberMut = useRemoveMember(id ?? '');

  const confirmDeleteDashboard = async () => {
    if (!deleteDash) return;
    try {
      await deleteDashboard.mutateAsync({
        id: deleteDash.id,
        auditLabel: `"${deleteDash.name}" · ${tenant?.name ?? 'tenant'}`,
      });
      toast.success(`Dashboard "${deleteDash.name}" deleted`);
      setDeleteDash(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const confirmRemoveMember = async () => {
    if (!removeMember) return;
    try {
      await removeMemberMut.mutateAsync(removeMember.id);
      toast.success(`${removeMember.name} removed from tenant`);
      setRemoveMember(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Remove failed');
    }
  };

  const confirmBatchDashboards = async () => {
    if (!batchDashes) return;
    let failed = 0;
    for (const dashId of batchDashes) {
      try {
        const dash = tenant?.dashboards?.items?.find((d) => d.id === dashId);
        await deleteDashboard.mutateAsync({
          id: dashId,
          auditLabel: dash ? `"${dash.name}" · ${tenant?.name ?? 'tenant'}` : undefined,
        });
      } catch {
        failed++;
      }
    }
    const n = batchDashes.length;
    setBatchDashes(null);
    dashSel.exit();
    if (failed > 0) toast.error(`${failed} of ${n} dashboards could not be deleted`);
    else toast.success(`${n} dashboard${n === 1 ? '' : 's'} deleted`);
  };

  const confirmBatchMembers = async () => {
    if (!batchMembers) return;
    let failed = 0;
    for (const m of batchMembers) {
      try {
        await removeMemberMut.mutateAsync(m.id);
      } catch {
        failed++;
      }
    }
    const n = batchMembers.length;
    setBatchMembers(null);
    memberSel.exit();
    if (failed > 0) toast.error(`${failed} of ${n} members could not be removed`);
    else toast.success(`${n} member${n === 1 ? '' : 's'} removed`);
  };

  const types = useMemo(
    () => [...new Set((tenants ?? []).map((t) => t.typeOfTenantID))].sort(),
    [tenants]
  );

  const filtered = useMemo(
    () =>
      (tenants ?? [])
        .filter((t) => typeFilter === 'all' || t.typeOfTenantID === typeFilter)
        .filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [tenants, search, typeFilter]
  );

  const visibleDashboards = useMemo(
    () =>
      (tenant?.dashboards?.items ?? [])
        .filter((d) => d.name.toLowerCase().includes(dashboardSearch.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [tenant, dashboardSearch]
  );
  const visibleDashboardIds = useMemo(() => visibleDashboards.map((d) => d.id), [visibleDashboards]);

  const visibleMembers = useMemo(
    () =>
      (tenant?.members?.items ?? [])
        .filter((m) => {
          const fullName = memberName(m);
          return (
            fullName.toLowerCase().includes(memberSearch.toLowerCase()) ||
            (m.profile.email ?? '').toLowerCase().includes(memberSearch.toLowerCase())
          );
        })
        .sort((a, b) => memberName(a).localeCompare(memberName(b))),
    [tenant, memberSearch]
  );
  const visibleMemberIds = useMemo(() => visibleMembers.map((m) => m.id), [visibleMembers]);

  // Arrow-key browsing: ↑/↓ moves the selection through the filtered list.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const target = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable) return;
      if (filtered.length === 0) return;
      e.preventDefault();
      const idx = filtered.findIndex((t) => t.id === id);
      const nextIdx =
        idx === -1
          ? 0
          : e.key === 'ArrowDown'
            ? Math.min(idx + 1, filtered.length - 1)
            : Math.max(idx - 1, 0);
      const next = filtered[nextIdx];
      if (next && next.id !== id) navigate(`/tenants/${next.id}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filtered, id, navigate]);

  // Keep the selected row visible while keyboard-browsing or deep-linking.
  useEffect(() => {
    if (id) {
      document.getElementById(`tenant-row-${id}`)?.scrollIntoView({ block: 'nearest' });
    }
  }, [id]);

  return (
    <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-[340px_minmax(0,1fr)] lg:gap-6 lg:h-[calc(100dvh-4rem)]">
      {/* ---- List pane (master) ---- */}
      <div className={cn('min-h-0 flex-col', id ? 'hidden lg:flex' : 'flex')}>
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          <div className="p-3 border-b border-border space-y-2">
            <div className="flex items-center justify-between">
              <h1 className="text-sm font-semibold">Tenants</h1>
              <Badge variant="secondary">{filtered.length}</Badge>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tenants…  (⌘K to jump)"
                className="pl-8 h-8 text-xs"
              />
            </div>
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="[&>select]:h-8 [&>select]:text-xs">
              <option value="all">All types</option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </div>

          <div className="min-h-0 flex-1 overflow-auto p-1.5 space-y-0.5">
            {tenantsLoading ? (
              <div className="space-y-1.5 p-1">
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 w-full" />
                ))}
              </div>
            ) : tenantsError ? (
              <EmptyState icon={AlertCircle} title="Failed to load tenants" description={tenantsError.message} className="m-2 py-8" />
            ) : filtered.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-muted-foreground">
                No tenants match {search && `“${search}”`} {typeFilter !== 'all' && `(${typeFilter})`}
              </p>
            ) : (
              filtered.map((t) => (
                <Link
                  key={t.id}
                  id={`tenant-row-${t.id}`}
                  to={`/tenants/${t.id}`}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-xs transition-colors',
                    id === t.id
                      ? 'bg-accent text-accent-foreground font-medium'
                      : 'text-foreground hover:bg-muted/60'
                  )}
                >
                  <span className="truncate">{t.name}</span>
                  <Badge variant="outline" className="shrink-0 text-[9px]">
                    {t.typeOfTenantID}
                  </Badge>
                </Link>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* ---- Detail pane ---- */}
      <div className={cn('min-h-0 lg:overflow-auto', !id && 'hidden lg:block')}>
        {!id ? (
          <div className="h-full flex items-center justify-center">
            <EmptyState
              icon={Users}
              title="Select a tenant"
              description="Pick from the list on the left, browse with ↑ / ↓, or press ⌘K to jump directly to a tenant."
              className="w-full"
            />
          </div>
        ) : tenantLoading ? (
          <div className="grid gap-4 xl:grid-cols-2">
            <Card className="p-5 space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </Card>
            <Card className="p-5 space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </Card>
          </div>
        ) : !tenant ? (
          <EmptyState
            icon={AlertCircle}
            title="Tenant not found"
            description="This tenant ID doesn't exist (anymore)."
            action={
              <Button variant="outline" size="sm" onClick={() => navigate('/tenants')}>
                Back to tenants
              </Button>
            }
          />
        ) : (
          <div className="space-y-4 animate-enter lg:h-full lg:flex lg:flex-col">
            <div className="flex items-center gap-2 shrink-0">
              <Tooltip label="Back to tenants" side="bottom">
                <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8" onClick={() => navigate('/tenants')} aria-label="Back to tenants">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Tooltip>
              <h2 className="text-lg font-semibold tracking-tight truncate">{tenant.name}</h2>
              <Badge variant="outline" className="shrink-0">{tenant.typeOfTenantID}</Badge>
              <Tooltip label="Open in Mission Control" side="bottom">
                <a
                  href={`${MC_BASE}/${tenant.id}/dashboards`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-input bg-background px-2.5 py-1.5 text-xs font-medium shadow-card transition-colors hover:bg-accent hover:text-accent-foreground ml-1"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Mission Control</span>
                </a>
              </Tooltip>
            </div>

            <div className="grid gap-4 xl:grid-cols-2 lg:flex-1 lg:min-h-[420px] lg:grid-rows-[1fr_1fr] xl:grid-rows-1">
              {/* ---- Dashboards ---- */}
              <Card className="p-5 lg:flex lg:min-h-0 lg:flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <LayoutDashboard className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold">Dashboards</h3>
                  <Badge variant="secondary">{tenant.dashboards?.items?.length ?? 0}</Badge>
                  {(tenant.dashboards?.items?.length ?? 0) > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto h-7 text-xs"
                      onClick={() => (dashSel.active ? dashSel.exit() : dashSel.start())}
                    >
                      {dashSel.active ? 'Cancel' : 'Select'}
                    </Button>
                  )}
                </div>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={dashboardSearch}
                    onChange={(e) => setDashboardSearch(e.target.value)}
                    placeholder="Search dashboards..."
                    className="pl-9 h-8 text-xs"
                  />
                </div>
                <div className="max-h-[50vh] lg:max-h-none lg:flex-1 lg:min-h-0 overflow-auto">
                  {visibleDashboards.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-6 text-center">No dashboards yet</p>
                  ) : (
                    visibleDashboards.map((d, di) => (
                      <div
                        key={d.id}
                        onClick={
                          dashSel.active
                            ? (e) => dashSel.toggle(d.id, di, visibleDashboardIds, e.shiftKey)
                            : undefined
                        }
                        className={cn(
                          'group px-1 py-3 text-sm transition-colors border-b border-border/60 last:border-b-0',
                          dashSel.active ? 'cursor-pointer select-none' : 'hover:bg-muted/40',
                          dashSel.active && dashSel.selected.has(d.id) && 'bg-accent/60'
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="flex items-center gap-2 min-w-0 flex-1">
                            {dashSel.active && (
                              <input
                                type="checkbox"
                                readOnly
                                checked={dashSel.selected.has(d.id)}
                                className="h-3.5 w-3.5 shrink-0 accent-primary pointer-events-none"
                              />
                            )}
                            <span className="font-medium text-xs leading-snug break-words" title={d.name}>
                              {d.name}
                            </span>
                          </span>
                          {!dashSel.active && (
                            <span className="flex items-center gap-0.5 shrink-0 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                              <Tooltip label="Copy to another tenant">
                                <button
                                  type="button"
                                  onClick={() => setCopyDash(d)}
                                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-background transition-colors"
                                  aria-label={`Copy ${d.name} to another tenant`}
                                >
                                  <CopyPlus className="h-3.5 w-3.5" />
                                </button>
                              </Tooltip>
                              <Tooltip label="Edit dashboard">
                                <button
                                  type="button"
                                  onClick={() => setEditDash({ id: d.id, name: d.name, description: d.description, sortOrder: d.sortOrder })}
                                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-background transition-colors"
                                  aria-label={`Edit ${d.name}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              </Tooltip>
                              <Tooltip label="Delete dashboard">
                                <button
                                  type="button"
                                  onClick={() => setDeleteDash({ id: d.id, name: d.name })}
                                  className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                  aria-label={`Delete ${d.name}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </Tooltip>
                            </span>
                          )}
                        </div>
                        <div className="mt-1.5 flex items-center justify-between gap-2">
                          <Badge variant="outline" className="min-w-0 max-w-[65%] text-[10px] font-normal">
                            <span className="truncate" title={d.template?.name ?? 'Custom'}>
                              {d.template?.name ?? 'Custom'}
                            </span>
                          </Badge>
                          <a
                            href={`${MC_BASE}/${d.tenantID}/dashboards/${d.id}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => dashSel.active && e.preventDefault()}
                            className={cn(
                              'inline-flex shrink-0 items-center gap-1 text-[11px] text-primary hover:underline',
                              dashSel.active && 'pointer-events-none opacity-60'
                            )}
                          >
                            <ExternalLink className="h-3 w-3 shrink-0" />
                            Open dashboard
                          </a>
                        </div>
                        {d.fixedParameters && d.fixedParameters.length > 0 && (
                          <div className="mt-1.5 text-[11px] text-muted-foreground">
                            Fixed: {d.fixedParameters.map((fp: any) => fp.label || fp.key).join(', ')}
                          </div>
                        )}
                        {d.variableFilterOverrides && d.variableFilterOverrides.length > 0 && (
                          <div className="mt-1 text-[11px] text-muted-foreground">
                            Overrides: {d.variableFilterOverrides.map((v: any) => v.label || v.key).join(', ')}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
                {dashSel.active && (
                  <SelectionBar
                    count={dashSel.selected.size}
                    actionLabel="Delete"
                    pending={deleteDashboard.isPending}
                    onAction={() => setBatchDashes([...dashSel.selected])}
                    onDone={dashSel.exit}
                  />
                )}
              </Card>

              {/* ---- Members ---- */}
              <Card className="p-5 lg:flex lg:min-h-0 lg:flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold">Members</h3>
                  <Badge variant="secondary">{tenant.members?.items?.length ?? 0}</Badge>
                  <span className="ml-auto flex items-center gap-1">
                    {(tenant.members?.items?.length ?? 0) > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => (memberSel.active ? memberSel.exit() : memberSel.start())}
                      >
                        {memberSel.active ? 'Cancel' : 'Select'}
                      </Button>
                    )}
                    {!memberSel.active && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setAddMemberOpen(true)}
                      >
                        <UserPlus className="h-3.5 w-3.5 mr-1" />
                        Add
                      </Button>
                    )}
                  </span>
                </div>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Search members..."
                    className="pl-9 h-8 text-xs"
                  />
                </div>
                <div className="space-y-0.5 max-h-[50vh] lg:max-h-none lg:flex-1 lg:min-h-0 overflow-auto pr-1">
                  {visibleMembers.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-6 text-center">No members</p>
                  ) : (
                    visibleMembers.map((m, mi) => {
                      const fullName = memberName(m);
                      return (
                        <div
                          key={m.id}
                          onClick={
                            memberSel.active
                              ? (e) => memberSel.toggle(m.id, mi, visibleMemberIds, e.shiftKey)
                              : undefined
                          }
                          className={cn(
                            'group flex items-center gap-3 rounded-md px-2.5 py-2 transition-colors',
                            memberSel.active ? 'cursor-pointer select-none' : 'hover:bg-muted/50',
                            memberSel.active && memberSel.selected.has(m.id) && 'bg-accent/60'
                          )}
                        >
                          {memberSel.active && (
                            <input
                              type="checkbox"
                              readOnly
                              checked={memberSel.selected.has(m.id)}
                              className="h-3.5 w-3.5 shrink-0 accent-primary pointer-events-none"
                            />
                          )}
                          <Avatar name={fullName} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium truncate" title={fullName}>{fullName}</span>
                              {m.tenantRole && m.tenantRole !== 'NONE' && (
                                <Badge variant="outline" className="shrink-0 text-[9px]">{m.tenantRole}</Badge>
                              )}
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate" title={m.profile.email ?? ''}>
                              {m.profile.email}
                            </div>
                          </div>
                          {!memberSel.active && (
                            <span className="flex items-center gap-0.5 shrink-0 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                              <Tooltip label="Change role">
                                <button
                                  type="button"
                                  onClick={() => setRoleMember({ id: m.id, name: fullName, tenantRole: m.tenantRole })}
                                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-background transition-colors"
                                  aria-label={`Edit role for ${fullName}`}
                                >
                                  <UserCog className="h-3.5 w-3.5" />
                                </button>
                              </Tooltip>
                              <Tooltip label="Remove from tenant">
                                <button
                                  type="button"
                                  onClick={() => setRemoveMember({ id: m.id, name: fullName })}
                                  className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                  aria-label={`Remove ${fullName}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </Tooltip>
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                {memberSel.active && (
                  <SelectionBar
                    count={memberSel.selected.size}
                    actionLabel="Remove"
                    pending={removeMemberMut.isPending}
                    onAction={() =>
                      setBatchMembers(
                        visibleMembers
                          .filter((m) => memberSel.selected.has(m.id))
                          .map((m) => ({ id: m.id, name: memberName(m) }))
                      )
                    }
                    onDone={memberSel.exit}
                  />
                )}
              </Card>
            </div>

            {/* Additional tenant capabilities — shown only when present */}
            {((tenant.documents?.items?.length ?? 0) > 0 ||
              (tenant.analyticsDashboards?.items?.length ?? 0) > 0 ||
              (tenant.inventoryDashboards?.items?.length ?? 0) > 0) && (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {(tenant.documents?.items?.length ?? 0) > 0 && (
                  <DocumentsCard tenantId={tenant.id} documents={tenant.documents.items} />
                )}
                {(tenant.analyticsDashboards?.items?.length ?? 0) > 0 && (
                  <LiveViewsCard tenantId={tenant.id} items={tenant.analyticsDashboards.items} />
                )}
                {(tenant.inventoryDashboards?.items?.length ?? 0) > 0 && (
                  <InventoryCard tenantId={tenant.id} items={tenant.inventoryDashboards.items} />
                )}
              </div>
            )}

            <EditDashboardDialog
              key={editDash?.id ?? 'none'}
              dashboard={editDash}
              tenantName={tenant.name}
              open={editDash !== null}
              onClose={() => setEditDash(null)}
            />
            <CopyDashboardDialog
              sourceTenantId={tenant.id}
              typeOfTenantID={tenant.typeOfTenantID}
              dashboard={copyDash}
              open={copyDash !== null}
              onClose={() => setCopyDash(null)}
            />
            <ConfirmDialog
              open={deleteDash !== null}
              title={`Delete dashboard "${deleteDash?.name ?? ''}"?`}
              description="The dashboard is removed for all members of this tenant and its Cluvio sharing link stops working."
              confirmLabel="Delete"
              destructive
              loading={deleteDashboard.isPending}
              onConfirm={confirmDeleteDashboard}
              onCancel={() => setDeleteDash(null)}
            />
            <ConfirmDialog
              open={batchDashes !== null}
              title={`Delete ${batchDashes?.length ?? 0} dashboards?`}
              description="Their Cluvio sharing links stop working for all members of this tenant."
              confirmLabel="Delete all"
              destructive
              loading={deleteDashboard.isPending}
              onConfirm={confirmBatchDashboards}
              onCancel={() => setBatchDashes(null)}
            />
            <AddMemberDialog
              tenantId={tenant.id}
              typeOfTenantID={tenant.typeOfTenantID}
              open={addMemberOpen}
              onClose={() => setAddMemberOpen(false)}
            />
            <EditRoleDialog
              key={roleMember?.id ?? 'none'}
              tenantId={tenant.id}
              member={roleMember}
              open={roleMember !== null}
              onClose={() => setRoleMember(null)}
            />
            <ConfirmDialog
              open={removeMember !== null}
              title={`Remove ${removeMember?.name ?? ''}?`}
              description="They lose access to this tenant and its dashboards. Their account itself is not deleted."
              confirmLabel="Remove"
              destructive
              loading={removeMemberMut.isPending}
              onConfirm={confirmRemoveMember}
              onCancel={() => setRemoveMember(null)}
            />
            <ConfirmDialog
              open={batchMembers !== null}
              title={`Remove ${batchMembers?.length ?? 0} members?`}
              description="They lose access to this tenant and its dashboards. Their accounts are not deleted."
              confirmLabel="Remove all"
              destructive
              loading={removeMemberMut.isPending}
              onConfirm={confirmBatchMembers}
              onCancel={() => setBatchMembers(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
